import Image from 'next/image';
import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';

import { type Workspace } from '@air/api/types';
import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

const WORKSPACE_LOGO_IMAGE_SIZE = 32;
const WORKSPACE_CHEVRON_SIZE = 14;

const getWorkspaceInitials = (name: string) => {
  const [firstWord, secondWord] = name.trim().split(/\s+/);

  if (!firstWord) {
    return '';
  }

  if (secondWord) {
    return `${firstWord.at(0) ?? ''}${secondWord.at(0) ?? ''}`.toUpperCase();
  }

  return firstWord.slice(0, 2).toUpperCase();
};

export type WorkspaceHeaderInformationDropdownTriggerProps = ComponentPropsWithoutRef<'button'> & {
  currentWorkspace: Pick<Workspace, 'name' | 'image'>;
};

export const WorkspaceHeaderInformationDropdownTrigger = memo(
  forwardRef<HTMLButtonElement, WorkspaceHeaderInformationDropdownTriggerProps>(
    ({ className, currentWorkspace, ...restOfProps }, ref) => {
      const logoSrc = getAvatarImageSrc(currentWorkspace.image, WORKSPACE_LOGO_IMAGE_SIZE);
      const initials = getWorkspaceInitials(currentWorkspace.name);

      return (
        <button
          className={cn(
            'group flex h-full min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-tl-12 px-3 font-medium ads:text-primary text-14 text-gray-12 outline-none transition-colors ads:hover:bg-hover-subtle hover:bg-gray-2 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset ads:data-[state=open]:bg-hover-subtle data-[state=open]:bg-gray-2',
            className,
          )}
          data-testid="CURRENT_WORKSPACE_BUTTON"
          ref={ref}
          type="button"
          {...restOfProps}
        >
          <span
            aria-hidden
            className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-8 border ads:border-default border-gray-4 ads:bg-raised bg-white dark:bg-gray-2"
          >
            {logoSrc ? (
              <Image alt="" className="object-cover" fill sizes="32px" src={logoSrc} />
            ) : (
              <span aria-hidden className="font-medium ads:text-primary text-10 text-gray-12">
                {initials}
              </span>
            )}
          </span>
          <span className="min-w-0 truncate" data-testid="WORKSPACE_HEADER_WORKSPACE_NAME">
            {currentWorkspace.name}
          </span>
          <span
            aria-hidden
            className="flex h-3.5 w-0 shrink-0 items-center overflow-hidden opacity-0 transition-[margin,opacity,width] group-hover:ml-1 group-hover:w-3.5 group-hover:opacity-100 group-focus-visible:ml-1 group-focus-visible:w-3.5 group-focus-visible:opacity-100 group-data-[state=open]:ml-1 group-data-[state=open]:w-3.5 group-data-[state=open]:opacity-100"
          >
            <ChevronDownIcon className="shrink-0 ads:text-icon-subtle text-gray-9" size={WORKSPACE_CHEVRON_SIZE} />
          </span>
        </button>
      );
    },
  ),
);

WorkspaceHeaderInformationDropdownTrigger.displayName = 'WorkspaceHeaderInformationDropdownTrigger';
